import { useLayoutEffect, useState } from 'react';

/** How close a hover box may come to the viewport edge before it is slid back. */
const EDGE_MARGIN = 8;

/** The band of the window the box can show in: cut by every clipping ancestor,
 *  unless the box is fixed, which they do not clip. */
const visibleBand = (node) => {
    const band = { top: 0, bottom: window.innerHeight };
    if (getComputedStyle(node).position === 'fixed') return band;
    for (let n = node.parentElement; n; n = n.parentElement) {
        if (getComputedStyle(n).overflowY === 'visible') continue;
        const r = n.getBoundingClientRect();
        band.top = Math.max(band.top, r.top);
        band.bottom = Math.min(band.bottom, r.bottom);
    }
    return band;
};

/**
 * Vertical correction that slides a hover box back inside the part of the
 * viewport it can show in — the window for a fixed box, the scrolling area it
 * sits in for one placed inside the page.
 *
 * `calculateTooltipPosition` has to place a box before it exists, from an
 * estimated height — and these boxes are content-sized: a plain ring runs about
 * 200px, a weapon with damages, meta and a legendary line more than twice that.
 * No single estimate covers both, so a tall one placed top-anchored hangs off
 * the bottom of the screen. The box therefore measures itself once it is up and
 * takes the space on the other side instead.
 *
 * The shift is capped by the room actually available, so correcting one edge
 * never pushes the opposite one out of reach: a box taller than the viewport
 * lands against the nearer edge rather than overshooting past both.
 *
 * @param {React.RefObject<HTMLElement>} ref - the positioned box
 * @param {any} key - changes whenever the box is re-placed or its content
 *   changes, which is when the measurement has to be taken again; null while
 *   the box is not placed yet
 * @returns {number} pixels to add to the box's `top`
 */
export function useViewportShift(ref, key) {
    const [shift, setShift] = useState(0);

    // Drop the previous correction first: measuring with it still applied would
    // read it as part of this placement's natural position.
    useLayoutEffect(() => {
        setShift(0);
    }, [key]);

    useLayoutEffect(() => {
        if (shift !== 0 || key == null) return;
        const node = ref.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const band = visibleBand(node);
        const below = rect.bottom - (band.bottom - EDGE_MARGIN);
        const above = (band.top + EDGE_MARGIN) - rect.top;
        if (below > 0) {
            const room = rect.top - (band.top + EDGE_MARGIN);
            if (room > 0) setShift(-Math.min(below, room));
        } else if (above > 0) {
            const room = (band.bottom - EDGE_MARGIN) - rect.bottom;
            if (room > 0) setShift(Math.min(above, room));
        }
    }, [ref, key, shift]);

    return shift;
}
